import type { LayerRect } from './types';
import { MIN_LAYER_SIZE } from './applyCanvasDrag';
import type { SnapGuides, SnapTarget } from './computeSnap';

type Corner = 'nw' | 'ne' | 'sw' | 'se';

const GUIDE_POSITION: Record<SnapTarget, number> = { start: 0, center: 0.5, end: 1 };

/** Center first, so an exact tie resolves to the midline. */
const TARGETS: SnapTarget[] = ['center', 'start', 'end'];

/** Nearest in-range guide for a single moving edge. Strict `<`, so a zero
 *  threshold disables the axis rather than latching onto a flush edge. */
function nearestGuide(
  edge: number,
  threshold: number,
): { delta: number; guide: SnapTarget | null } {
  let best: { delta: number; guide: SnapTarget | null } = { delta: 0, guide: null };
  for (const target of TARGETS) {
    const delta = GUIDE_POSITION[target] - edge;
    if (Math.abs(delta) < threshold && (best.guide === null || Math.abs(delta) < Math.abs(best.delta))) {
      best = { delta, guide: target };
    }
  }
  return best;
}

/** Pull the MOVING edges of a corner-resize onto the composition's guides.
 *
 *  Unlike move-snapping (a translation), resize-snapping adjusts size: only the
 *  edges the dragged corner owns may move, and the anchored corner never does.
 *
 *  - Unlocked (text): the two moving edges snap independently.
 *  - Aspect-locked (image/video): one snap must drive both dimensions, so the
 *    NEAREST in-range axis wins — normalized by its threshold, which compares
 *    on-screen pixel distances even though the fractions differ per axis — and
 *    the other dimension follows the rect's aspect, scaled about the anchor.
 *
 *  Any candidate that would push a dimension below MIN_LAYER_SIZE is skipped,
 *  matching applyCanvasResize's floor. */
export function computeResizeSnap(
  { rect, corner, lockAspect, thresholdX, thresholdY }: {
    rect: LayerRect;
    corner: Corner;
    lockAspect: boolean;
    thresholdX: number;
    thresholdY: number;
  },
): { rect: LayerRect; guides: SnapGuides } {
  const movingRight = corner.includes('e');
  const movingBottom = corner.includes('s');
  const right = rect.x + rect.w;
  const bottom = rect.y + rect.h;

  const sx = nearestGuide(movingRight ? right : rect.x, thresholdX);
  const sy = nearestGuide(movingBottom ? bottom : rect.y, thresholdY);

  // The dragged corner's edge moves toward the guide by `delta`; the resulting
  // width/height depends on which edge is anchored.
  const widthAfter = (delta: number) => (movingRight ? rect.w + delta : rect.w - delta);
  const heightAfter = (delta: number) => (movingBottom ? rect.h + delta : rect.h - delta);

  if (!lockAspect) {
    let { x, w } = rect;
    let { y, h } = rect;
    let guideX = sx.guide;
    let guideY = sy.guide;

    if (guideX !== null && widthAfter(sx.delta) >= MIN_LAYER_SIZE) {
      w = widthAfter(sx.delta);
      if (!movingRight) x = rect.x + sx.delta;
    } else {
      guideX = null;
    }
    if (guideY !== null && heightAfter(sy.delta) >= MIN_LAYER_SIZE) {
      h = heightAfter(sy.delta);
      if (!movingBottom) y = rect.y + sy.delta;
    } else {
      guideY = null;
    }
    return { rect: { x, y, w, h }, guides: { x: guideX, y: guideY } };
  }

  // Aspect-locked: one snap drives both dimensions. |delta| / threshold is the
  // distance in units of the threshold, i.e. proportional to screen pixels, so
  // the comparison is fair even though the fractional thresholds differ.
  const ratio = rect.w > 0 && rect.h > 0 ? rect.w / rect.h : 0;
  if (ratio <= 0) return { rect, guides: { x: null, y: null } };

  const candidates: Array<{ axis: 'x' | 'y'; w: number; h: number; dist: number; guide: SnapTarget }> = [];
  if (sx.guide !== null) {
    const w = widthAfter(sx.delta);
    candidates.push({ axis: 'x', w, h: w / ratio, dist: Math.abs(sx.delta) / thresholdX, guide: sx.guide });
  }
  if (sy.guide !== null) {
    const h = heightAfter(sy.delta);
    candidates.push({ axis: 'y', w: h * ratio, h, dist: Math.abs(sy.delta) / thresholdY, guide: sy.guide });
  }

  for (const c of candidates.sort((a, b) => a.dist - b.dist)) {
    if (c.w < MIN_LAYER_SIZE || c.h < MIN_LAYER_SIZE) continue;
    return {
      rect: {
        x: movingRight ? rect.x : right - c.w,
        y: movingBottom ? rect.y : bottom - c.h,
        w: c.w,
        h: c.h,
      },
      guides: c.axis === 'x' ? { x: c.guide, y: null } : { x: null, y: c.guide },
    };
  }
  return { rect, guides: { x: null, y: null } };
}
